import { z } from "zod";
import { slugString } from "./slug.js";
import { authoredText, requiredAuthoredText } from "../authoredText.js";
import { Locale } from "../locale.js";
import { ProgramTargets } from "./programTargets.js";

// The weekly-targets schemas live in their own module now; re-exported here
// so the ~20 existing `./schemas/program.js` importers keep working.
export * from "./programTargets.js";

// Multi-week training program. Redesigned after the SL parity
// assessment: weeks first-class, sessionsPerWeek explicit, per-slot
// variant cycling, per-slot progression rule, AMRAP / 1MAX placement.
// SL's data model (Program → Week → Workout, weeks frozen copies of
// each other) was rejected as "rigido e barboso".
//
// Phase 5.1 (this commit) ships only the foundation — schema + CRUD +
// list view. Slot/variant/progression builder UI lands in 5.2; week
// resolution + auto AMRAP/1MAX placement in 5.3; "follow program" home
// surface in 5.4. The schema below is the full target shape so future
// phases don't need a migration.

const ExerciseSlug = slugString(64);

// Per-set intensity prescription. Distinct kinds because the resolver
// (Phase 5.3) and the workout logger render each one differently:
//
//   none           — no prescribed intensity. Athlete picks the weight
//                    in-session. Common for warm-ups, mobility holds,
//                    technique-only sets.
//   absolute       — fixed weight in kg. The program author types the
//                    number, resolved as-is at plan-start. Used when
//                    MAX isn't tracked for the lift, or when the
//                    program author overrides on a per-cycle basis.
//   percent-of-max — the program author types a percentage of MAX.
//                    Resolved against the athlete's live MAX at
//                    plan-start, so a MAX update before execution
//                    shifts targets without re-editing the program.
//   history        — explicit "use the athlete's last-logged weight on
//                    this exercise". Equivalent to `none` at the data
//                    layer (no prescribed kg) but distinct intent: the
//                    coach is asking the resolver to look back, not
//                    leaving the cell blank. resolveDefaultWeight reads
//                    this kind and falls through to the history step.
//   rpe            — target RPE 1..10, weight chosen by the athlete to
//                    hit that exertion level. The resolver keeps it as
//                    an RPE target on the planned set.
//   rir            — target RIR 0..10 (reps-in-reserve), weight chosen by
//                    the athlete. Canonical effort kind: RIR is JL's
//                    internal effort scale (see effortScale.ts). The
//                    Logger renders whichever scale the athlete prefers,
//                    converting at the edge. RIR 0 = to failure.
export const SetIntensity = z.discriminatedUnion("kind", [
  z.object({ kind: z.literal("none") }),
  z.object({
    kind: z.literal("absolute"),
    weightKg: z.number().min(-500).max(2000),
  }),
  z.object({
    kind: z.literal("percent-of-max"),
    pct: z.number().min(0).max(200),
  }),
  z.object({ kind: z.literal("history") }),
  z.object({
    kind: z.literal("rpe"),
    target: z.number().min(1).max(10),
  }),
  z.object({
    kind: z.literal("rir"),
    target: z.number().min(0).max(10),
  }),
]);
export type SetIntensity = z.infer<typeof SetIntensity>;

// VARIANT CADENCE — when this variant is the active exercise for the
// slot. A slot with two variants {Leg Press @ every-other phase 0,
// Hack Squat @ every-other phase 1} alternates the two each week.
//
//   every-week  — always active (single-variant slot — most common)
//   alternating — active when (week - phaseOffset) % 2 == 0
//   third-week  — active when (week - phaseOffset) % 3 == 0
//
// phaseOffset shifts the cycle. Multi-variant slots typically have
// non-zero offsets so the cadence covers every week.
export const VariantCadence = z.enum(["every-week", "alternating", "third-week"]);
export type VariantCadence = z.infer<typeof VariantCadence>;

export const Variant = z.object({
  exerciseId: ExerciseSlug,
  // Snapshot at write time; server stamps from catalog so the program
  // view never depends on the catalog still containing the slug.
  exerciseName: z.string().min(1).max(120).optional(),
  cadence: VariantCadence,
  phaseOffset: z.number().int().min(0).max(3),
});
export type Variant = z.infer<typeof Variant>;

// WORK AXIS — how this set is BOUNDED in time (HI-1, next/interval-
// training.md §9.1). Absent = the reps-bounded set that has always
// existed, so every program written before this delta reads back
// unchanged and no migration exists.
//
//   countdown — a bout of durationSec. The work timer runs it down and
//               stamps the completion at zero, which is the same effect
//               the athlete's tap has today.
//   countup   — an open bout. The athlete's tap closes it (For Time, a
//               free hold), exactly as a reps set closes today.
//
// ⛔ There is deliberately NO third field for "how this set advances":
// the advance mode is DERIVED (countdown → automatic; countup or no
// `work` at all → the existing completion tap). One tap raises one
// prompt, and a stored mode could contradict the bound.
export const SetWork = z.discriminatedUnion("kind", [
  z.object({
    kind: z.literal("countdown"),
    // Twin of Exercise.defaultDurationSec — same `*Sec` suffix the
    // prescription side uses everywhere (defaultRestSec, defaultDurationSec).
    // The stored RESULT stays LogSet.duration, bare, as it is today.
    durationSec: z.number().int().min(1).max(3600),
  }),
  z.object({ kind: z.literal("countup") }),
]);
export type SetWork = z.infer<typeof SetWork>;

// SET PRESCRIPTION — one planned set inside a single week of a slot.
// reps + intensity carry the prescription. reps == 0 with intensity.kind
// == "none" is the AMRAP / freestyle case (the athlete writes reps in
// session). per-set notes override the slot-wide instructions for that
// one set: pause length, tempo override, "stop two reps short".
export const SetPrescription = z.object({
  reps: z.number().int().min(0).max(50),
  // Per-set AMRAP ("5+"): reps stays the FLOOR the athlete must reach,
  // the flag tells the logger to render "5+ reps". Additive 2026-06-11
  // (composer reps syntax "N+"); the older slot-level amrapLastSet
  // keeps working and the resolver ORs the two.
  amrap: z.boolean().optional(),
  intensity: SetIntensity,
  // Time axis (HI-1). Both optional and both additive: absent `work`
  // means a reps set, absent `restSec` means the rest ladder decides
  // (exercise.defaultRestSec → profile.defaultRestSec), which is what
  // every set does today. A HIIT bout prescribes both: "30s of work,
  // then 90s of rest", eight times.
  work: SetWork.optional(),
  restSec: z.number().int().min(0).max(3600).optional(),
  notes: authoredText(200).optional(),
});
export type SetPrescription = z.infer<typeof SetPrescription>;

// WEEK PLAN — the set list for one week of the cycle. The slot's weeks[]
// array has length equal to Program.cycleWeeks, indexed week-1 = 0.
// Set count is uniform across the slot's ACTIVE weeks (a slot's "shape"
// is N sets each week, with reps and intensity varying per cell).
//
// sets: [] means the slot is INACTIVE that week — "this exercise only
// runs W1-W3 of a 6-week cycle". Introduced for the scheduler-composer
// editor (P0, next/program-editor-redesign.md §3.1): a block that
// targets a subset of weeks expands to empty WeekPlans on the others.
// The resolver yields no entry for an inactive week, and a (session,
// week) whose slots are all inactive is skipped by next-session math
// (sessionHasEntriesForWeek). Programs written before this delta are
// all min(1)-shaped, so no migration.
export const WeekPlan = z.object({
  sets: z.array(SetPrescription).min(0).max(20),
});
export type WeekPlan = z.infer<typeof WeekPlan>;

// Same shape as WorkoutEntryGroup / PlannedEntryGroup — when ≥2 slots
// in the same session carry the same group.id, the resolver tags the
// resulting PlannedExerciseEntries with that group, so the workout
// logger renders them as a superset/circuit window. Singletons are
// dropped at resolution time (matches the rest of the stack).
// GROUP CADENCE — the RHYTHM of the group, as opposed to `work` above,
// which is what one set IS (HI-1, next/interval-training.md §9.2). The
// split is the reason EMOM and AMRAP don't demote the set back to a
// stopwatch: the set stays the thing you do, the group says the pace.
//
//   rounds — repeat the group N times (a circuit, the cycles of a Tabata)
//   amrap  — repeat until the cap, counting the rounds you finished
//   emom   — every round is a fixed window
//
// ⚠️ Absent = today's behaviour exactly: rounds are the set count and
// the athlete advances by hand. The first HIIT cut is ONE exercise and
// does not read this — it is stored now, with the time axis above,
// because both move `tooling/api-contract` and one deploy is cheaper
// than two. The UI that authors and runs it lands with the circuit
// slice, and PlannedEntryGroup / WorkoutEntryGroup grow the twin field
// with it — deliberately NOT before, so the three shapes never carry a
// field that only one of them can honour.
export const GroupCadence = z.discriminatedUnion("kind", [
  z.object({
    kind: z.literal("rounds"),
    count: z.number().int().min(1).max(99),
  }),
  z.object({
    kind: z.literal("amrap"),
    timeCapSec: z.number().int().min(1).max(7200),
  }),
  z.object({
    kind: z.literal("emom"),
    windowSec: z.number().int().min(1).max(3600),
    rounds: z.number().int().min(1).max(99),
  }),
]);
export type GroupCadence = z.infer<typeof GroupCadence>;

export const SlotGroup = z.object({
  id: z.string().min(1).max(64),
  kind: z.enum(["superset", "circuit"]),
  cadence: GroupCadence.optional(),
  // Same optional label WorkoutEntryGroup already carries (workout.ts:267).
  // A program could hold a superset but not its NAME, so the one the picker
  // asks for was typed and then dropped on save — the program editor mounts
  // that same picker (PE-26). Absent = the client renders its default
  // "Superset N" label, so old programs keep the numbering they have.
  name: z.string().trim().min(1).max(60).optional(),
});
export type SlotGroup = z.infer<typeof SlotGroup>;

// SLOT — one planned exercise position in a session. Multi-variant
// slots cycle exercises across weeks. The slot owns the per-week per-set
// matrix and the slot-wide AMRAP / 1MAX flags.
//
// `instructions` is the slot-wide cue text shown during workout
// execution: tempo, pause length, technique focus. Per-set notes
// override at the individual set level when only one set deviates.
export const Slot = z.object({
  id: z.string().min(1),
  position: z.number().int().min(0).max(50),
  variants: z.array(Variant).min(1).max(4),
  weeks: z.array(WeekPlan).min(1).max(12),
  // AMRAP on the last working set, repeated every session in the cycle.
  // Q6 default at program-create time: AMRAP on the primary lift's
  // session in cycles ≥ 4 weeks (off in deload). User overrides here.
  amrapLastSet: z.boolean().optional(),
  // 1MAX attempt on the test week (last week of cycle, before deload).
  // Q6 default placed by the editor; user override here.
  maxAttempt: z.boolean().optional(),
  instructions: authoredText(500).optional(),
  // Per-slot default for the Logger's RPE column. Propagated onto the
  // resolved PlannedExerciseEntry.rpeEnabled by programResolution.ts so
  // downstream consumers read a single field regardless of whether the
  // session originated from a Plan or a Program slot.
  rpeEnabled: z.boolean().optional(),
  // Per-slot default for the Logger's RIR column. Same propagation as
  // rpeEnabled. Phase 8.
  rirEnabled: z.boolean().optional(),
  // Optional grouping. Forwarded to the resolved
  // PlannedExerciseEntry.group when this slot is active for the
  // resolved week.
  group: SlotGroup.optional(),
});
export type Slot = z.infer<typeof Slot>;

// SESSION — one training session in the program template. Sessions are
// ordinal: the array index is the canonical position in the week (1st,
// 2nd, 3rd...). No dayOfWeek binding, because in real life a Monday
// session that doesn't fit just shifts to Tuesday or gets dropped, and
// the program shouldn't pretend otherwise.
export const ProgramSession = z.object({
  id: z.string().min(1),
  name: requiredAuthoredText(80),
  // min(1): an empty slots array would let the resolver yield a Plan
  // with entries: [] which then trips Plan.entries.min(1) at the read
  // boundary — better to reject the empty session up-front when the
  // program is saved.
  slots: z.array(Slot).min(1).max(20),
  notes: authoredText(500).optional(),
});
export type ProgramSession = z.infer<typeof ProgramSession>;

// PROGRAM — the top-level entity. cycleWeeks bounds the resolution
// horizon (1..12); sessionsPerWeek is the weekly training-frequency
// metadata (1..7) and is independent of sessions.length — Stronglifts
// 5×5 has two session shapes (A, B) but a 3× / week cadence, so the
// schema doesn't equate the two. assignedTo follows the same
// coach→athlete shape as Plan: an array of subs the program is
// assigned to, defaulting to [createdBy] for self-coaching.
export const ProgramStatus = z.enum(["draft", "active", "archived"]);
export type ProgramStatus = z.infer<typeof ProgramStatus>;

export const Program = z.object({
  id: z.string().min(1),
  createdBy: z.string().min(1),
  assignedTo: z.array(z.string().min(1)).min(1).max(20),
  name: requiredAuthoredText(120),
  description: authoredText(2000).optional(),
  // The language the program was WRITTEN in (PE-21). Optional, and
  // absent on every program written before it existed. It is the second
  // link of the read chain — reader → author → any available — so a
  // program written in French doesn't fall back to English just because
  // an English translation exists somewhere in the object. Programs
  // without it fall straight through to "any available", which is what
  // they did before.
  authorLocale: Locale.optional(),
  cycleWeeks: z.number().int().min(1).max(12),
  sessionsPerWeek: z.number().int().min(1).max(7),
  sessions: z.array(ProgramSession).min(1).max(7),
  // Optional weekly training targets (AP-1). Absent on most programs; the
  // adaptive meter + loop light up only when present.
  targets: ProgramTargets.optional(),
  status: ProgramStatus,
  createdAt: z.iso.datetime(),
  updatedAt: z.iso.datetime(),
});
export type Program = z.infer<typeof Program>;

// Active-follow marker. Stamped server-side every time a session is
// created with a fromPlanId of the form `prog/<programId>/wk<n>/sess<m>/...`.
// One row per (user, programId) at sk=PROGPROG#<programId>; updates
// last-* in place so the row tracks the most recently completed slot.
// Drives the ProgramsCard "next session" pre-selection on the start
// form; no progression / cycling logic lives here, the client computes
// the next slot from these three fields plus program.cycleWeeks /
// sessions.length.
export const ProgramProgress = z.object({
  programId: z.string().min(1),
  lastWeekIdx: z.number().int().min(0).max(11),
  lastSessionIdx: z.number().int().min(0).max(6),
  // MC-8 — stable, reorder-safe link to the last completed session. When
  // present it wins over the positional lastSessionIdx (nextSlotFor), so
  // reordering the program's sessions doesn't misplace "next up". Optional:
  // pre-MC-8 markers carry only the ordinal, and the row auto-heals on the
  // next session save (the marker is rewritten in full every time).
  lastSessionId: z.string().min(1).optional(),
  lastCompletedAt: z.iso.datetime(),
});
export type ProgramProgress = z.infer<typeof ProgramProgress>;

// Parser for the program-derived fromPlanId composite. Returns null when
// the value isn't a program-shaped composite (legacy single-day plans,
// importer rows). Tolerant of trailing segments — the canonical shape is
// `prog/<programId>/wk<n>/sess<m>` optionally followed by `/sid<sessionId>`
// (MC-8, the stable reorder-safe link) and any other suffix. `sessionId` is
// read from the `sid` segment wherever it sits; pre-MC-8 rows have none and
// resolve positionally by sessionIdx.
export const parseProgramFromPlanId = (
  raw: string | undefined,
): {
  programId: string;
  weekIdx: number;
  sessionIdx: number;
  sessionId?: string;
} | null => {
  if (!raw) return null;
  if (!raw.startsWith("prog/")) return null;
  const parts = raw.split("/");
  if (parts.length < 4) return null;
  const programId = parts[1];
  const wkPart = parts[2];
  const sessPart = parts[3];
  if (!programId || !wkPart || !sessPart) return null;
  if (!wkPart.startsWith("wk") || !sessPart.startsWith("sess")) return null;
  const weekIdx = Number.parseInt(wkPart.slice(2), 10);
  const sessionIdx = Number.parseInt(sessPart.slice(4), 10);
  if (!Number.isFinite(weekIdx) || weekIdx < 0) return null;
  if (!Number.isFinite(sessionIdx) || sessionIdx < 0) return null;
  const sidPart = parts.slice(4).find((p) => p.startsWith("sid"));
  const sessionId = sidPart ? sidPart.slice(3) : "";
  return {
    programId,
    weekIdx,
    sessionIdx,
    ...(sessionId ? { sessionId } : {}),
  };
};

// The inverse of parseProgramFromPlanId: build the program-derived fromPlanId
// composite. One home so mobile and web-rnw (the two workout starters) can't
// drift the format apart — the very kind of divergence MC-8 exists to prevent.
// The ordinal `sess<m>` stays as a fallback rail; `/sid<sessionId>` is the
// authoritative reorder-safe link.
export const buildProgramFromPlanId = (
  programId: string,
  weekIdx: number,
  sessionIdx: number,
  sessionId: string,
): string => `prog/${programId}/wk${weekIdx}/sess${sessionIdx}/sid${sessionId}`;

// Wire-shape variants: ids are optional on creation. Server stamps a
// UUID when missing and preserves the provided value when an edit
// PATCH round-trips an existing session/slot. The stored Program
// always has non-empty ids since stampIds runs before every Put.
export const NewSlot = Slot.extend({
  id: z.string().min(1).max(64).optional(),
});
export type NewSlot = z.infer<typeof NewSlot>;

export const NewProgramSession = ProgramSession.extend({
  id: z.string().min(1).max(64).optional(),
  // Same min(1) as the stored shape — wire and storage stay symmetric so
  // the parse boundary is the only place an empty session can be
  // rejected. Templates and the future builder both fill at least one
  // slot before submitting.
  slots: z.array(NewSlot).min(1).max(20),
});
export type NewProgramSession = z.infer<typeof NewProgramSession>;

// Client → server payload. Server fills id, createdBy, createdAt,
// updatedAt and stamps session/slot ids when missing. Status defaults
// to "draft" if absent. assignedTo is optional on the wire because
// self-coaching is the common case: the server fills [callerSub]
// when the client omits it or sends an empty array. The stored Program
// always has a non-empty assignedTo (validated at the typed layer).
export const NewProgram = Program.omit({
  id: true,
  createdBy: true,
  createdAt: true,
  updatedAt: true,
  assignedTo: true,
  sessions: true,
}).extend({
  // Optional client-generated UUID. Same offline-first contract as
  // NewPlan: client mints the id locally, server preserves it, replays
  // with the same id return 409 duplicate_id.
  id: z.uuid().optional(),
  sessions: z.array(NewProgramSession).min(1).max(7),
  assignedTo: z.array(z.string().min(1)).max(20).optional(),
  status: ProgramStatus.optional(),
  // Same email-resolution path as NewPlan: server resolves emails to
  // subs via Cognito and merges into assignedTo. Skip if you only want
  // self-coaching.
  assignedToEmails: z.array(z.email()).max(20).optional(),
});
export type NewProgram = z.infer<typeof NewProgram>;
